import { createSlice } from '@reduxjs/toolkit'
import { setSuccessNotification, setErrorNotification } from './notificationReducer'
import { parseErrorMessage } from '../services/utils.js/errorParser'
import resourcesService from '../services/resourceService'

const PERMISSION_DENIED = 'Permission Denied: You must be a staff to perform this action'

const initialResourceState = {
    results: [],
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
    loading: false
}

/**
 * Bind the unified /api/resources/ service to a single django-polymorphic
 * subclass token so the factory can keep its `service: {retrieve, retrieveSingle,
 * create, update, destroy}` shape. Terminology and any other non-polymorphic
 * resource pass its own service object instead.
 */
export const typedResourceService = (resourcetype) => ({
    retrieve: (query = '', page = 1) => resourcesService.retrieve(resourcetype, query, page),
    retrieveSingle: (id) => resourcesService.retrieveSingle(id),
    create: (data) => resourcesService.create(resourcetype, data),
    update: (id, data) => resourcesService.update(id, data, resourcetype),
    destroy: (id) => resourcesService.destroy(id),
})

/**
 * Factory that creates a Redux slice plus thunk action creators for a
 * standard paginated resource. `service` must expose retrieve(query, page),
 * retrieveSingle(id), create(data), update(id, data), destroy(id).
 */
export const createResourceReducer = ({ sliceName, label, labelPlural, service, permissionDeniedMessage }) => {
    const permDenied = permissionDeniedMessage || PERMISSION_DENIED
    const labelP = (labelPlural || `${label}s`).toLowerCase()

    const slice = createSlice({
        name: sliceName,
        initialState: initialResourceState,
        reducers: {
            setResources(state, action) {
                return action.payload
            },
            setLoading(state, action) {
                state.loading = action.payload
            },
            appendResource(state, action) {
                if (state.results) {
                    state.results.push(action.payload)
                    state.count += 1
                }
            },
            destroyResource(state, action) {
                if (state.results) {
                    state.results = state.results.filter(item => item.id !== action.payload)
                    state.count -= 1
                }
            },
            changeResource(state, action) {
                if (state.results) {
                    state.results = state.results.map(item =>
                        item.id === action.payload.id ? action.payload : item
                    )
                }
            }
        }
    })

    const { setResources, setLoading, appendResource, destroyResource, changeResource } = slice.actions

    // Last-issued-wins for the list (issue #407): pagination/search can have several
    // retrieves in flight, and without this the slowest response, not the newest
    // request, decided what the grid showed. Shared by initialize and search because
    // both replace the same slice.
    let latestListRequest = 0

    const createResource = (data) => {
        return async dispatch => {
            try {
                const resource = await service.create(data)
                dispatch(appendResource(resource))
                dispatch(setSuccessNotification(`${label} created successfully`))
                return true
            } catch (error) {
                const errorMessage = parseErrorMessage(error, 'Error submitting request, please try again later')
                dispatch(setErrorNotification(`Error creating ${label.toLowerCase()}: ${errorMessage}`))
                return false
            }
        }
    }

    const initializeResources = (page = 1) => {
        return async dispatch => {
            const requestId = ++latestListRequest
            try {
                dispatch(setLoading(true))
                const data = await service.retrieve('', page)
                if (requestId !== latestListRequest) return
                dispatch(setResources({ ...data, loading: false }))
            } catch (error) {
                if (requestId !== latestListRequest) return // a superseded failure must not toast or clear the newer request's spinner
                console.error(`Failed to retrieve ${labelP}:`, error)
                dispatch(setErrorNotification(`Error retrieving ${labelP}`))
                dispatch(setLoading(false))
            }
        }
    }

    const deleteResource = (id, admin) => {
        return async dispatch => {
            if (admin) {
                try {
                    await service.destroy(id)
                    dispatch(destroyResource(id))
                    dispatch(setSuccessNotification(`${label} deleted successfully`))
                } catch (error) {
                    console.error(`Failed to delete ${label.toLowerCase()} id=${id}:`, error)
                    dispatch(setErrorNotification(`Error deleting ${label.toLowerCase()}`))
                }
            } else {
                dispatch(setErrorNotification(permDenied))
            }
        }
    }

    const updateResource = (id, data, admin) => {
        return async dispatch => {
            if (admin) {
                try {
                    const resource = await service.update(id, data)
                    dispatch(changeResource(resource))
                    dispatch(setSuccessNotification(`${label} updated successfully`))
                    return true
                } catch (error) {
                    const errorMessage = parseErrorMessage(error, `Update of ${label.toLowerCase()} failed`)
                    dispatch(setErrorNotification(`Update failed: ${errorMessage}`))
                    return false
                }
            } else {
                dispatch(setErrorNotification(permDenied))
                return false
            }
        }
    }

    const searchResources = (query, page = 1) => {
        return async dispatch => {
            const requestId = ++latestListRequest
            try {
                dispatch(setLoading(true))
                const data = await service.retrieve(query, page)
                if (requestId !== latestListRequest) return
                dispatch(setResources({ ...data, loading: false }))
            } catch (error) {
                if (requestId !== latestListRequest) return // a superseded failure must not toast or clear the newer request's spinner
                console.error(`Failed to search ${labelP} (query=${query}):`, error)
                dispatch(setErrorNotification('Error fulfilling search'))
                dispatch(setLoading(false))
            }
        }
    }

    const refreshResource = (id) => {
        return async dispatch => {
            try {
                const resource = await service.retrieveSingle(id)
                dispatch(changeResource(resource))
            } catch (error) {
                console.error(`Failed to refresh ${label.toLowerCase()} id=${id}:`, error)
                dispatch(setErrorNotification(`Error refreshing ${label.toLowerCase()}`))
            }
        }
    }

    return {
        reducer: slice.reducer,
        actions: { setResources, setLoading, appendResource, destroyResource, changeResource },
        thunks: { createResource, initializeResources, deleteResource, updateResource, searchResources, refreshResource }
    }
}
